import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../shared/LoadingSpinner';
import ConfirmDialog from '../shared/ConfirmDialog';

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmAdmin, setConfirmAdmin] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    api.adminGetUser(id)
      .then(({ user, memberships, logins }) => {
        setUser(user);
        setMemberships(memberships || []);
        setLogins(logins || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const toggleAdmin = async () => {
    try {
      const updated = await api.adminUpdateUser(id, { is_platform_admin: !user.is_platform_admin });
      setUser(prev => ({ ...prev, is_platform_admin: updated.is_platform_admin }));
    } catch {
      // leave state unchanged on failure
    }
    setConfirmAdmin(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await api.deletePlatformUser(id);
      setConfirmDelete(false);
      navigate('/admin/users', { replace: true });
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const isSelf = profile?.id === id;

  // Best reachable number: personal phone first, else the phone of the org
  // they created (primary org / owner) — that's the number they gave at
  // signup — else any of their orgs' phones.
  const orgPhone =
    memberships.find(m => (m.is_primary_org || m.role === 'owner') && m.organization?.phone)?.organization?.phone
    || memberships.find(m => m.organization?.phone)?.organization?.phone
    || null;
  const bestPhone = user?.phone || orgPhone;

  if (loading) {
    return <div className="flex-1 flex items-center justify-center bg-primary"><LoadingSpinner size="lg" /></div>;
  }

  if (!user) {
    return <div className="flex-1 flex items-center justify-center bg-primary"><p className="text-muted-c">User not found</p></div>;
  }

  return (
    <div className="flex-1 overflow-y-auto bg-primary">
      <div className="max-w-[800px] mx-auto px-4 md:px-7 py-6">
        <button onClick={() => navigate('/admin/users')} className="text-[12px] text-muted-c hover:text-accent-light transition-colors mb-1">
          &larr; Back to users
        </button>

        {/* User info */}
        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-[52px] h-[52px] rounded-[14px] gradient-accent flex items-center justify-center text-[18px] font-semibold text-white">
                {(user.first_name?.[0] || '')}{(user.last_name?.[0] || '')}
              </div>
              <div>
                <h1 className="text-[20px] font-bold text-primary-c">{user.first_name} {user.last_name}</h1>
                <p className="text-[13px] text-muted-c">{user.email}</p>
              </div>
            </div>
            {user.is_platform_admin && (
              <span className="text-[11px] font-semibold text-accent-light bg-accent-glow px-2.5 py-1 rounded-full">
                Platform Admin
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <div className="text-[11px] text-muted-c mb-0.5">Phone</div>
              <div className="text-[13px] text-secondary-c">
                {bestPhone
                  ? <>
                      <a href={`tel:${bestPhone}`} className="hover:text-accent-light transition-colors">{bestPhone}</a>
                      {!user.phone && <span className="text-[11px] text-muted-c"> (org)</span>}
                    </>
                  : '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-muted-c mb-0.5">Joined</div>
              <div className="text-[13px] text-secondary-c">{new Date(user.created_at).toLocaleDateString()}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-c mb-0.5">Last sign-in</div>
              <div className="text-[13px] text-secondary-c">
                {user.last_sign_in_at
                  ? formatDistanceToNow(new Date(user.last_sign_in_at), { addSuffix: true })
                  : 'Never'}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-theme gap-3">
            {!isSelf ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="px-4 py-2 rounded-[10px] text-[13px] font-semibold text-danger border border-danger/30 hover:bg-danger-soft transition-all"
              >
                Delete User
              </button>
            ) : (
              <span className="text-[11px] text-muted-c italic">This is your account</span>
            )}
            <button
              onClick={() => setConfirmAdmin(true)}
              className={`px-4 py-2 rounded-[10px] text-[13px] font-semibold transition-all ${
                user.is_platform_admin
                  ? 'text-danger bg-danger-soft hover:bg-[rgba(244,63,94,0.2)]'
                  : 'gradient-accent text-white shadow-glow hover:-translate-y-0.5'
              }`}
            >
              {user.is_platform_admin ? 'Remove Admin' : 'Make Admin'}
            </button>
          </div>
          {deleteError && (
            <div className="mt-3 px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium">
              {deleteError}
            </div>
          )}
        </div>

        {/* Memberships */}
        <div className="bg-elevated border border-theme rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-theme">
            <h2 className="text-[15px] font-semibold text-primary-c">Org Memberships ({memberships.length})</h2>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {memberships.map(m => (
              <div key={m.id} className="flex items-center justify-between px-5 py-3.5">
                <div>
                  <div className="text-[13px] font-semibold text-primary-c">{m.organization?.name}</div>
                  <div className="text-[11px] text-muted-c capitalize">
                    {m.organization?.type?.replace('_', ' ')}
                    {m.organization?.phone && (
                      <> · <a href={`tel:${m.organization.phone}`} className="hover:text-accent-light transition-colors normal-case">{m.organization.phone}</a></>
                    )}
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-secondary-c bg-tertiary px-2.5 py-1 rounded-full capitalize">
                  {m.role?.replace('_', ' ')}
                </span>
              </div>
            ))}
            {!memberships.length && (
              <div className="px-5 py-8 text-center text-[13px] text-muted-c">No memberships</div>
            )}
          </div>
        </div>

        {/* Login history (recorded from 2026-07 onward) */}
        <div className="bg-elevated border border-theme rounded-xl overflow-hidden mt-6">
          <div className="px-5 py-4 border-b border-theme">
            <h2 className="text-[15px] font-semibold text-primary-c">Recent logins</h2>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {logins.map(l => (
              <div key={l.id} className="flex items-center justify-between px-5 py-3 gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] text-primary-c">{new Date(l.created_at).toLocaleString()}</div>
                  {l.user_agent && (
                    <div className="text-[11px] text-muted-c truncate max-w-[420px]">{l.user_agent}</div>
                  )}
                </div>
                {l.ip && <span className="text-[11px] text-muted-c shrink-0">{l.ip}</span>}
              </div>
            ))}
            {!logins.length && (
              <div className="px-5 py-8 text-center text-[13px] text-muted-c">
                No logins recorded yet — history starts from the day login tracking shipped.
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmAdmin}
        title={user.is_platform_admin ? 'Remove Admin Privileges' : 'Grant Admin Privileges'}
        message={user.is_platform_admin
          ? `Remove platform admin access from ${user.first_name} ${user.last_name}?`
          : `Grant platform admin access to ${user.first_name} ${user.last_name}? They will be able to manage all organizations and users.`}
        confirmLabel={user.is_platform_admin ? 'Remove Admin' : 'Make Admin'}
        danger={user.is_platform_admin}
        onConfirm={toggleAdmin}
        onCancel={() => setConfirmAdmin(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete user"
        message={`Permanently delete ${user.first_name} ${user.last_name} (${user.email})? This removes their login, profile, and all associated records. This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting\u2026' : 'Delete User'}
        danger
        onConfirm={handleDelete}
        onCancel={() => { setConfirmDelete(false); setDeleteError(''); }}
      />
    </div>
  );
}
